import { ObjectId } from '@freightview/object-id'
import { defaults, isNil, padStart } from 'lodash-es'
import type { ChangeEvent } from 'react'

import type {
  ClientPackagingType,
  HazardousDetailFormModel,
  LegacyHandlingUnitDTO,
  LegacyHazardDetail,
  LegacyReadOnlyDetail,
  LoadHandlingUnit,
  ProductCatalogEntry,
  ProductMode,
  YesNo,
} from '@fv/client-types'
import type {
  DimensionUOM,
  LoadItem,
  PackagingType,
  Workflow,
} from '@fv/models'
import type { DTO } from '@fv/models/core'

import type { FormCommodity } from './FormCommodity'

const palletDimMap: Partial<
  Record<ClientPackagingType, { width: number; length: number }>
> = {
  'pallet-48x48': {
    width: 48,
    length: 48,
  },
  'pallet-48x40': {
    width: 40,
    length: 48,
  },
  'pallet-60x40': {
    width: 40,
    length: 60,
  },
}

export const normalizePackageType = (
  type: ClientPackagingType,
): PackagingType => {
  if (
    type === 'pallet-48x40' ||
    type === 'pallet-48x48' ||
    type === 'pallet-60x40'
  ) {
    return 'pallet'
  }
  return type
}

export const dimsFromPackageType = (
  type: ClientPackagingType,
): { length?: string; width?: string } => {
  const palletMatch = palletDimMap[type]
  if (palletMatch) {
    return {
      width: palletMatch.width.toString(),
      length: palletMatch.length.toString(),
    }
  } else if (type === 'pallet') {
    return {
      length: '',
      width: '',
    }
  }
  return {}
}

export const packageTypeFromDims = (
  type: ClientPackagingType | '',
  workflow: Workflow,
  dims: {
    length?: number
    width?: number
  },
): ClientPackagingType | '' => {
  if (!/pallet/.test(type)) {
    return type
  }

  if (workflow === 'truckload') {
    return 'pallet'
  }

  const match = Object.entries(palletDimMap).find(
    ([k, v]) => v.length === dims.length && v.width === dims.width,
  )?.[0] as ClientPackagingType

  return match ?? 'pallet'
}

export function buildHazardousDetails(
  item?: Partial<LoadHandlingUnit>,
): HazardousDetailFormModel {
  const overrides = item?.hazardous?.[0]

  return {
    hazmatId: undefined,
    packingGroup: undefined,
    permitNumber: undefined,
    primaryClass: undefined,
    secondaryClass: undefined,
    tertiaryClass: undefined,
    properShippingName: undefined,
    technicalName: undefined,
    reportableQuantity: undefined,
    residueLastContained: undefined,
    limitedQuantity: undefined,
    marinePollutant: undefined,
    ...overrides,
  }
}

export function buildFormCommodity({
  dropSequence = 1,
  item,
  pickSequence = 0,
}: {
  dropSequence?: number
  item?: Partial<LoadHandlingUnit> | DTO<LoadItem>
  pickSequence?: number
}): FormCommodity {
  return {
    declaredValue: String(item?.declaredValue || ''),
    declaredValueCurrency: item?.declaredValueCurrency || 'usd',
    description: item?.description || '',
    dimensionsUOM: 'in',
    dropSequence,
    freightClass: String(item?.freightClass || ''),
    handlingUnitId: item?.handlingUnitId || '',
    hazardous: Array.isArray(item?.hazardous) ? 'Yes' : 'No',
    height: String(item?.height || ''),
    id: item?._id ?? new ObjectId().toHexString(),
    length: String(item?.length || ''),
    nmfc: displayNmfc(item),
    nonStandard: item?.nonStandard ? 'Yes' : 'No',
    pickSequence,
    quantity: String(item?.quantity || ''),
    saidToContain: String(item?.contains?.[0]?.quantity || ''),
    saidToContainPackagingType: item?.contains?.[0]?.type || '',
    stackable: item?.stackable ? 'Yes' : 'No',
    type: item?.type || 'pallet',
    weight: String(item?.weight || ''),
    weightUOM: item?.weightUOM || 'lbs',
    width: String(item?.width || ''),
    ...buildHazardousDetails(item),
  }
}

export function buildLegacyHazardDetail(
  values?: Partial<LegacyHazardDetail>,
): LegacyHazardDetail {
  return {
    hazardClass: values?.hazardClass ?? '',
    hazmatId: values?.hazmatId ?? '',
    packingGroup: values?.packingGroup ?? '',
    secondaryHazardClass: values?.secondaryHazardClass ?? '',
    specialPermit: values?.specialPermit ?? '',
    tertiaryHazardClass: values?.tertiaryHazardClass ?? '',
    properShippingName: values?.properShippingName ?? '',
    technicalName: values?.technicalName ?? '',
    reportableQuantity: values?.reportableQuantity ?? false,
    residueLastContained: values?.residueLastContained ?? false,
    limitedQuantity: values?.limitedQuantity ?? false,
    marinePollutant: values?.marinePollutant ?? false,
  }
}

const defaultReadOnly: LegacyReadOnlyDetail = {
  declaredValue: false,
  description: false,
  freightClass: false,
  hazardClass: false,
  hazardous: false,
  hazmatId: false,
  height: false,
  length: false,
  nmfc: false,
  nonStandard: false,
  package: false,
  packingGroup: false,
  pieces: false,
  saidToContain: false,
  saidToContainPackagingType: false,
  secondaryHazardClass: false,
  specialPermit: false,
  stackable: false,
  tertiaryHazardClass: false,
  weight: false,
  width: false,
  properShippingName: false,
  technicalName: false,
  reportableQuantity: false,
  residueLastContained: false,
  limitedQuantity: false,
  marinePollutant: false,
}

export function buildProductCatalogEntry(
  mode: ProductMode,
  unit?: LegacyHandlingUnitDTO,
): ProductCatalogEntry {
  const {
    reportableQuantity,
    residueLastContained,
    limitedQuantity,
    marinePollutant,
    ...hazardStrings
  } = buildLegacyHazardDetail(unit?.hazard)

  return {
    _id: unit?._id ?? '',
    autoGenerated: unit?.autoGenerated ?? false,
    declaredValue: String(unit?.declaredValue?.amount ?? ''),
    description: unit?.description ?? '',
    freightClass: String(unit?.freightClass ?? ''),
    hazardous: unit?.hazardous ? 'Yes' : 'No',
    height: String(unit?.height ?? ''),
    length: String(unit?.length ?? ''),
    mode: unit?.mode ?? mode,
    nmfc: unit?.nmfc ?? '',
    nonStandard: unit?.nonStandard ? 'Yes' : 'No',
    package:
      unit?.package === undefined
        ? mode === 'parcel'
          ? 'custom'
          : 'pallet'
        : (unit.package ?? ''),
    pieces: String(unit?.pieces ?? ''),
    readOnly: defaults(unit?.readOnly ?? defaultReadOnly, defaultReadOnly),
    saidToContain: String(unit?.saidToContain ?? ''),
    saidToContainPackagingType: unit?.saidToContainPackagingType ?? '',
    stackable: unit?.stackable ? 'Yes' : 'No',
    weight: String(unit?.weight ?? ''),
    width: String(unit?.width ?? ''),
    ...hazardStrings,
    reportableQuantity: reportableQuantity ? 'Yes' : 'No',
    residueLastContained: residueLastContained ? 'Yes' : 'No',
    limitedQuantity: limitedQuantity ? 'Yes' : 'No',
    marinePollutant: marinePollutant ? 'Yes' : 'No',
  }
}

export function buildLegacyHandlingUnitDTO(
  product: ProductCatalogEntry,
): LegacyHandlingUnitDTO {
  const {
    declaredValue,
    freightClass,
    hazardClass,
    hazmatId,
    nmfc,
    packingGroup,
    saidToContainPackagingType,
    secondaryHazardClass,
    specialPermit,
    tertiaryHazardClass,
    height,
    length,
    pieces,
    saidToContain,
    weight,
    width,
    properShippingName,
    technicalName,
    reportableQuantity,
    residueLastContained,
    limitedQuantity,
    marinePollutant,
  } = product

  return {
    _id: product._id,
    autoGenerated: product.autoGenerated,
    description: product.description,
    hazardous: product.hazardous === 'Yes',
    mode: product.mode,
    nonStandard: product.nonStandard === 'Yes',
    readOnly: product.readOnly,
    stackable: product.stackable === 'Yes',
    ...(declaredValue && {
      declaredValue: {
        amount: Number(declaredValue),
        currency: 'USD',
      },
    }),
    ...(freightClass && { freightClass: Number(freightClass) }),
    ...(height && { height: Number(height) }),
    ...(length && { length: Number(length) }),
    ...(nmfc && { nmfc }),
    ...(pieces && { pieces: Number(pieces) }),
    ...(product.hazardous === 'Yes' && {
      hazard: {
        ...(hazardClass && { hazardClass }),
        ...(hazmatId && { hazmatId }),
        ...(packingGroup && { packingGroup }),
        ...(secondaryHazardClass && { secondaryHazardClass }),
        ...(specialPermit && { specialPermit }),
        ...(tertiaryHazardClass && { tertiaryHazardClass }),
        ...(properShippingName && { properShippingName }),
        ...(technicalName && { technicalName }),
        ...(reportableQuantity === 'Yes' && { reportableQuantity: true }),
        ...(residueLastContained === 'Yes' && { residueLastContained: true }),
        ...(limitedQuantity === 'Yes' && { limitedQuantity: true }),
        ...(marinePollutant === 'Yes' && { marinePollutant: true }),
      },
    }),
    ...(product.package && { package: normalizePackageType(product.package) }),
    ...(saidToContain && { saidToContain: Number(saidToContain) }),
    ...(saidToContainPackagingType && { saidToContainPackagingType }),
    ...(weight && { weight: Number(weight) }),
    ...(width && { width: Number(width) }),
  }
}
export function validateHazmatId(hazmatId: string) {
  const isValid = !hazmatId || /^(UN|NA)\d{4}$/.test(hazmatId)
  const message = isValid ? '' : 'Use UN#### or NA#### format.'
  return message
}

export function validateHazmatIdChange(e: ChangeEvent<HTMLInputElement>) {
  const hazmatId = e.target.value.toLocaleUpperCase()
  e.target.value = hazmatId

  e.target.setCustomValidity(validateHazmatId(hazmatId))
}

export function validateNmfc(nmfc: string) {
  const valid = !nmfc || /^\d{4,6}(-\d{1,2}){0,1}$/i.test(nmfc)
  return valid ? '' : 'Invalid NMFC'
}
export function validateNmfcChange(e: ChangeEvent<HTMLInputElement>) {
  const nmfc = e.target.value
  const message = validateNmfc(nmfc)
  e.target.setCustomValidity(message)
}

export function getDimensions(item: {
  dimensionsUOM?: DimensionUOM
  height?: number | string
  length?: number | string
  width?: number | string
}): string {
  const { dimensionsUOM = 'in', length, width, height } = item
  if (!length && !width && !height) return ''

  let dimensions = ''
  if (length) dimensions += `${length}Lx`
  if (width) dimensions += `${width}Wx`
  if (height) dimensions += `${height}Hx`
  dimensions = dimensions.replace(/x$/, '') // remove trailing `x`

  return `${dimensions} ${dimensionsUOM}`
}

export function hasHazardousItems(
  loads: Array<{ items: Array<{ hazardous: YesNo }> }>,
) {
  return loads.some(load => load.items.some(i => i.hazardous === 'Yes'))
}

export const getWorkflowProductMode = (workflow?: Workflow): ProductMode =>
  workflow === 'parcel' ? 'parcel' : 'ltl'

export const displayNmfc = (item?: {
  nmfcNumber?: number | string
  nmfcSubNumber?: number
}) =>
  item?.nmfcNumber
    ? !isNil(item.nmfcSubNumber)
      ? `${item.nmfcNumber}-${padStart(item.nmfcSubNumber.toString(), 2, '0')}`
      : String(item.nmfcNumber)
    : ''

export const parseNmfc = (
  nmfc?: string,
): Pick<LoadHandlingUnit, 'nmfcNumber' | 'nmfcSubNumber'> => {
  const split = nmfc?.split('-')
  const nmfcNumber = Number(split?.[0])
  const subNumber = (split?.length ?? 0) > 1 ? Number(split?.[1]) : undefined
  return {
    nmfcNumber: !nmfcNumber || isNaN(nmfcNumber) ? undefined : nmfcNumber,
    nmfcSubNumber: !subNumber || isNaN(subNumber) ? undefined : subNumber,
  }
}
